import { DashboardReportsUpsertDto } from './dto/dashboard-reports-upsert.dto';
import { EcomSettingsDto } from './dto/ecom-settings.dto';
import { DirectorySettingsDto } from './dto/directory-settings.dto';
import { ConfigServiceApiService } from './../../shared/api/config-service-api.service';
import { Injectable } from '@angular/core';
import { ReportMenuDto } from './dto/report-menu.dto';
import { DashboardConfigDto } from './dto/dashboard-config.dto';
import { ProfileSelectorConfigDto } from 'src/app/profiles/search/dto/profile-selector-config.dto';
import { FieldTypeEnum } from 'src/app/shared/search-expression/field-type.enum';
import { ProfileSelectorFieldEnum } from 'src/app/profiles/search/dto/profile-selector-field.enum';
import { ProfileSelectorSelectedFieldDto } from 'src/app/profiles/search/dto/profile-selector-selected-field.dto';
import { ProfileSelectorFieldTypeEnum } from 'src/app/profiles/search/dto/profile-selector-field-type.enum';
import { StandardReportDto } from 'src/app/reports/shared/dto/standard-report.dto';
import { CalendarSettingsDto } from 'src/app/admin/global-settings/shared/dto/calendar-settings.dto';
import { TermlySettingsDto } from 'src/app/admin/global-settings/shared/dto/termly-settings.dto';
import { WebComponentSettingsDto } from 'src/app/communication/public-links/shared/dto/web-component-settings.dto';
import { PortalMenuSettingsDto } from 'src/app/admin/global-settings/shared/dto/portal-menu-settings.dto';
import { EmailNotificationSettingsDto } from '../../admin/global-settings/shared/dto/email-notification-settings.dto';

@Injectable({
  providedIn: 'root',
})
export class ConfigServiceService {
  public constructor(private _apiService: ConfigServiceApiService) {}

  public getCalendarSettings(settingBatch: string = ''): Promise<CalendarSettingsDto> {
    return this._apiService.get(`/config/v2/calendarsettings?settingBatch=${settingBatch}`);
  }
  public getCalendarSettingBatches(): Promise<Array<string>> {
    return this._apiService.get(`/config/v2/calendarsettings/get-all-setting-batches`);
  }
  public getTermlySettings(settingBatch: string = ''): Promise<TermlySettingsDto> {
    return this._apiService.get(`/config/v2/termlySettings?settingBatch=${settingBatch}`);
  }
  public getDirectorySettings(settingBatch: string = ''): Promise<DirectorySettingsDto> {
    return this._apiService.get(`/config/v2/directorysettings?settingBatch=${settingBatch}`);
  }
  public getDirectorySettingBatches(): Promise<Array<string>> {
    return this._apiService.get(`/config/v2/directorysettings/get-all-setting-batches`);
  }

  public getEcomSettings(settingBatch: string = null): Promise<EcomSettingsDto> {
    return this._apiService.get(`/config/v2/ecomsettings?settingBatch=${settingBatch}`);
  }

  public getEcomSettingBatches(): Promise<Array<string>> {
    return this._apiService.get(`/config/v2/ecomsettings/get-all-setting-batches`);
  }

  public upsertEcomSettings(dto: EcomSettingsDto, settingBatch: string = null): Promise<EcomSettingsDto> {
    return this._apiService.post(`/config/v2/ecomsettings/upsert?settingBatch=${settingBatch}`, dto);
  }

  public getPortalMenuSettings(settingBatch: string = null): Promise<PortalMenuSettingsDto> {
    return this._apiService.get(`/config/v2/portalmenusettings?settingBatch=${settingBatch}`);
  }

  public getPortalMenuSettingBatches(): Promise<Array<string>> {
    return this._apiService.get(`/config/v2/portalmenusettings/get-all-setting-batches`);
  }

  public upsertPortalMenuSettings(dto: PortalMenuSettingsDto, settingBatch: string = null): Promise<PortalMenuSettingsDto> {
    return this._apiService.post(`/config/v2/portalmenusettings/upsert?settingBatch=${settingBatch}`, dto);
  }

  public getWebComponentSettings(settingBatch: string = '', forceRefresh?: boolean): Promise<WebComponentSettingsDto> {
    const cacheOptions = forceRefresh ? this._apiService.getDefaultApiCacheOptionsForNoCache() : null;
    return this._apiService.get(`/config/v2/webcomponentsettings?settingBatch=${settingBatch}`, cacheOptions);
  }

  public upsertWebComponentSettings(dto: WebComponentSettingsDto, settingBatch: string = ''): Promise<WebComponentSettingsDto> {
    return this._apiService.post(`/config/v2/webcomponentsettings/upsert?settingBatch=${settingBatch}`, dto);
  }

  public getEmailNotificationSettings(settingBatch: string = '', forceRefresh?: boolean): Promise<EmailNotificationSettingsDto> {
    const cacheOptions = forceRefresh ? this._apiService.getDefaultApiCacheOptionsForNoCache() : null;
    return this._apiService.get(`/config/v2/emailnotificationsettings?settingBatch=${settingBatch}`, cacheOptions);
  }

  public upsertEmailNotificationSettings(
    dto: EmailNotificationSettingsDto,
    settingBatch: string = ''
  ): Promise<EmailNotificationSettingsDto> {
    return this._apiService.post(`/config/v2/emailnotificationsettings/upsert?settingBatch=${settingBatch}`, dto);
  }

  public upsertTermlySettings(dto: TermlySettingsDto, settingBatch: string = ''): Promise<TermlySettingsDto> {
    return this._apiService.post(`/config/v2/termlysettings/upsert?settingBatch=${settingBatch}`, dto);
  }
  public upsertDirectorySettings(dto: DirectorySettingsDto, settingBatch: string = ''): Promise<DirectorySettingsDto> {
    return this._apiService.post(`/config/v2/directorysettings/upsert?settingBatch=${settingBatch}`, dto);
  }
  public upsertCalendarSettings(dto: CalendarSettingsDto, settingBatch: string = ''): Promise<DirectorySettingsDto> {
    return this._apiService.post(`/config/v2/calendarsettings/upsert?settingBatch=${settingBatch}`, dto);
  }

  public uploadEcomHeaderImage(image: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', image, image.name);
    return this._apiService.postRawTextReturn(`/config/v2/ecomsettings/upload-header-image`, formData);
  }

  public uploadDirectoryHeaderImage(image: File, settingBatch: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', image, image.name);
    return this._apiService.postRawTextReturn(`/config/v2/directorysettings/upload-header-image/?settingBatch=${settingBatch}`, formData);
  }

  public uploadCalendarHeaderImage(image: File, settingBatch: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', image, image.name);
    return this._apiService.postRawTextReturn(`/config/v2/calendarsettings/upload-header-image/?settingBatch=${settingBatch}`, formData);
  }

  public getFullReportsMenu(): Promise<ReportMenuDto[]> {
    return this._apiService.get('config/v2/reportmenu');
  }

  public getTableFriendlyStandardReports(): Promise<StandardReportDto[]> {
    return this._apiService.get('config/v2/reportmenu/TableFriendlyStandardReports');
  }

  public upsertDashboardReports(reports: DashboardReportsUpsertDto): Promise<any> {
    return this._apiService.post(`config/v2/reportmenu/mydashboardreports/upsert`, reports);
  }

  public getUserDashboardSettings(userNum: number): Promise<DashboardConfigDto> {
    return this._apiService.get(`/config/v2/dashboardconfig?userNum=${userNum}`);
  }

  public upsertUserDashboardSettings(dto: DashboardConfigDto): Promise<number> {
    return this._apiService.post(`/config/v2/dashboardconfig/upsert`, dto);
  }

  public checkForProfileSearchConfig(userNum: string): Promise<boolean> {
    return this._apiService.get(`/config/v2/profileselectorconfig/${userNum}`).then((data) => {
      if (!data) {
        return false;
      } else {
        return true;
      }
    });
  }

  public getProfileSearchConfig(userNum: string): Promise<ProfileSelectorConfigDto> {
    return this._apiService.get(`/config/v2/profileselectorconfig/${userNum}`).then((data) => {
      if (!data) {
        return this.getDefaultProfileSearchConfig();
      } else {
        return data;
      }
    });
  }

  public deleteProfileSearchConfig(userNum: string) {
    this._apiService.delete(`/config/v2/profileselectorconfig/${userNum}`);
  }

  public upsertProfileSearchConfig(config: ProfileSelectorConfigDto) {
    return this._apiService.post(`/config/v2/profileselectorconfig/upsert`, config);
  }

  public getFriendlyFieldNames() {
    return this._apiService.get(`/config/v2/profileselectorconfig/friendly-field-names`);
  }

  public getDefaultProfileSearchConfig() {
    return {
      SelectedFields: [
        {
          StandardFieldId: ProfileSelectorFieldEnum.ProfileID,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.ProfileName,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.RelatedOrganization,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.MainProfileEmail,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.MembershipLevel,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.ProfileStatus,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
        {
          StandardFieldId: ProfileSelectorFieldEnum.Email,
          FieldTypeEnum: ProfileSelectorFieldTypeEnum.StandardField,
        } as ProfileSelectorSelectedFieldDto,
      ],
    } as ProfileSelectorConfigDto;
  }

  public async getCkeditor5License(): Promise<string> {
    let cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
    return this._apiService.get(`/config/v2/Ckeditor/license`, cacheOptions);
  }

  public getCkeditor5Token(): Promise<string> {
    return this._apiService.get(`/config/v2/Ckeditor/token`);
  }
}
